/*
 * AdminFolhaPontoTab — convocações mensais de envio da foto da folha
 * de ponto. Admin escolhe a competência + funcionários (por posto ou
 * planilha), acompanha envios, aprova/rejeita e baixa o PDF único.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  CalendarClock, Plus, Loader2, Download, Trash2, Eye,
  CheckCircle2, XCircle, Clock, Upload, Image as ImageIcon,
  FileSpreadsheet,
} from "lucide-react";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const compLabel = (c) => {
  if (!c) return "";
  const [y, m] = c.split("-");
  const nomes = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  return `${nomes[parseInt(m, 10) - 1]}/${y}`;
};

const STATUS_BADGE = {
  pending: { label: "Pendente", cls: "bg-slate-100 text-slate-600" },
  submitted: { label: "Enviado", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Aprovado", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejeitado", cls: "bg-red-100 text-red-700" },
};

export default function AdminFolhaPontoTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null); // request being viewed
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [excelId, setExcelId] = useState(null);

  const handlePendingExcel = async (req) => {
    setExcelId(req.id);
    try {
      const r = await axios.get(
        `${API}/admin/folha-ponto/requests/${req.id}/pending-excel`,
        { headers: authHeader(), responseType: "blob", timeout: 60000 }
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pendentes_folha_ponto_${req.competencia}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel de pendentes baixado");
    } catch {
      toast.error("Erro ao gerar Excel");
    } finally {
      setExcelId(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/folha-ponto/requests`, {
        headers: authHeader(), timeout: 20000,
      });
      setItems(r.data.items || []);
    } catch {
      toast.error("Erro ao carregar convocações");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePdf = async (req) => {
    setDownloadingId(req.id);
    try {
      const r = await axios.get(
        `${API}/admin/folha-ponto/requests/${req.id}/pdf`,
        { headers: authHeader(), responseType: "blob", timeout: 180000 }
      );
      const url = URL.createObjectURL(
        new Blob([r.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `folhas_ponto_${req.competencia}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let d = "Erro ao gerar PDF";
      try {
        const t = await e?.response?.data?.text?.();
        if (t) d = JSON.parse(t).detail || d;
      } catch (_) { /* ignore */ }
      toast.error(d);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (req) => {
    if (!window.confirm(`Excluir a convocação de ${compLabel(req.competencia)}? As fotos recebidas serão apagadas.`)) return;
    setDeletingId(req.id);
    try {
      await axios.delete(`${API}/admin/folha-ponto/requests/${req.id}`, {
        headers: authHeader(),
      });
      toast.success("Convocação excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-folha-ponto-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-cyan-700" />
            Folha de Ponto
          </h2>
          <p className="text-xs text-slate-500">
            Convoque funcionários para enviar a foto da folha de ponto do mês.
            O app deles fica bloqueado até o envio.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
          data-testid="folha-new-request-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nova convocação
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin inline" />
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-slate-500">
          Nenhuma convocação criada ainda.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((req) => {
            const c = req.counts || {};
            const received = (c.submitted || 0) + (c.approved || 0);
            return (
              <Card key={req.id} data-testid={`folha-request-${req.id}`}>
                <CardContent className="p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">
                      {compLabel(req.competencia)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {req.count} convocado(s) • Criada em{" "}
                      {new Date(req.created_at).toLocaleString("pt-BR")}
                    </div>
                    <div className="text-xs mt-1 flex flex-wrap gap-2">
                      <span className="text-blue-700 font-semibold">
                        {received} recebida(s)
                      </span>
                      {(c.approved || 0) > 0 && (
                        <span className="text-emerald-700">{c.approved} aprovada(s)</span>
                      )}
                      {(c.rejected || 0) > 0 && (
                        <span className="text-red-600">{c.rejected} rejeitada(s)</span>
                      )}
                      {(c.pending || 0) > 0 && (
                        <span className="text-amber-700">{c.pending} pendente(s)</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button variant="outline" size="sm"
                      onClick={() => setDetail(req)}
                      data-testid={`folha-view-${req.id}`}
                      title="Ver envios">
                      <Eye className="w-4 h-4" />
                    </Button>
                    {((c.pending || 0) + (c.rejected || 0)) > 0 && (
                      <Button variant="outline" size="sm"
                        onClick={() => handlePendingExcel(req)}
                        disabled={excelId === req.id}
                        className="text-amber-700 hover:bg-amber-50"
                        data-testid={`folha-pending-excel-${req.id}`}
                        title="Excel dos pendentes (nome, CPF, posto)">
                        {excelId === req.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <FileSpreadsheet className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button variant="outline" size="sm"
                      onClick={() => handlePdf(req)}
                      disabled={downloadingId === req.id || received === 0}
                      data-testid={`folha-pdf-${req.id}`}
                      title="Baixar PDF único com as fotos">
                      {downloadingId === req.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Download className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm"
                      onClick={() => handleDelete(req)}
                      disabled={deletingId === req.id}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`folha-delete-${req.id}`}
                      title="Excluir convocação">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateRequestDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />
      <SubmissionsDialog
        request={detail}
        onClose={() => { setDetail(null); load(); }}
      />
    </div>
  );
}

/* -------------------- criação da convocação -------------------- */
function CreateRequestDialog({ open, onOpenChange, onCreated }) {
  const [competencia, setCompetencia] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [postos, setPostos] = useState([]);
  const [posto, setPosto] = useState("");
  const [contratos, setContratos] = useState([]);
  const [contrato, setContrato] = useState("");
  const [employees, setEmployees] = useState([]);
  const [checked, setChecked] = useState({});
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || postos.length) return;
    axios.get(`${API}/admin/postos`, { headers: authHeader() })
      .then((r) => setPostos(r.data.postos || r.data.items || r.data || []))
      .catch(() => {});
    axios.get(`${API}/admin/contratos`, { headers: authHeader() })
      .then((r) => setContratos(r.data.contracts || []))
      .catch(() => {});
  }, [open, postos.length]);

  useEffect(() => {
    if (open) { setChecked({}); setEmployees([]); setPosto(""); setContrato(""); }
  }, [open]);

  const fetchPostoEmployees = async (p) => {
    let page = 1, all = [];
    while (page < 20) {
      const r = await axios.get(`${API}/admin/employees`, {
        params: { posto: p, page, per_page: 200 },
        headers: authHeader(), timeout: 30000,
      });
      const list = r.data.employees || r.data.items || [];
      all = all.concat(list);
      if (list.length < 200) break;
      page += 1;
    }
    return all;
  };

  const loadPosto = async (p) => {
    setPosto(p);
    setContrato("");
    if (!p) { setEmployees([]); setChecked({}); return; }
    setLoadingEmps(true);
    try {
      const all = await fetchPostoEmployees(p);
      setEmployees(all);
      const next = {};
      all.forEach((e) => { next[e.id] = true; });
      setChecked(next);
    } catch {
      toast.error("Erro ao carregar funcionários do posto");
    } finally {
      setLoadingEmps(false);
    }
  };

  const loadContrato = async (name) => {
    setContrato(name);
    setPosto("");
    if (!name) { setEmployees([]); setChecked({}); return; }
    const c = contratos.find((x) => x.contract === name);
    if (!c) return;
    setLoadingEmps(true);
    try {
      const lists = await Promise.all(
        (c.postos || []).map((p) => fetchPostoEmployees(p.posto))
      );
      const seen = new Set();
      const all = [];
      lists.flat().forEach((e) => {
        if (!seen.has(e.id)) { seen.add(e.id); all.push(e); }
      });
      all.sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
      setEmployees(all);
      const next = {};
      all.forEach((e) => { next[e.id] = true; });
      setChecked(next);
      toast.success(`${all.length} funcionário(s) do contrato ${name}`);
    } catch {
      toast.error("Erro ao carregar funcionários do contrato");
    } finally {
      setLoadingEmps(false);
    }
  };

  const handleXlsx = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/employees/match-from-xlsx`, fd, {
        timeout: 60000, headers: authHeader(),
      });
      const matched = r.data?.matched || [];
      setEmployees(matched);
      const next = {};
      matched.forEach((m) => { next[m.id] = true; });
      setChecked(next);
      setPosto("");
      setContrato("");
      const nf = r.data?.not_found || [];
      toast.success(`${matched.length} funcionário(s) encontrados${nf.length ? ` • ${nf.length} não localizados` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao importar planilha");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Busca individual — adiciona à seleção sem substituir a lista atual.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/admin/employees`, {
          params: { search: q, page: 1, per_page: 10 },
          headers: authHeader(), timeout: 15000,
        });
        setSearchResults(r.data.employees || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const addIndividual = (emp) => {
    setEmployees((prev) =>
      prev.some((e) => e.id === emp.id) ? prev : [...prev, emp]
    );
    setChecked((prev) => ({ ...prev, [emp.id]: true }));
    setSearch("");
    setSearchResults([]);
    toast.success(`${emp.nome} adicionado`);
  };

  const selectedIds = useMemo(
    () => Object.entries(checked).filter(([, v]) => v).map(([k]) => k),
    [checked]
  );

  const handleCreate = async () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione ao menos um funcionário");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/folha-ponto/requests`, {
        competencia, employee_ids: selectedIds,
      }, { headers: authHeader(), timeout: 60000 });
      toast.success(
        `Convocação criada: ${r.data.count} funcionário(s)` +
        (r.data.skipped ? ` (${r.data.skipped} já convocados antes)` : "")
      );
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar convocação");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nova convocação de folha de ponto</DialogTitle>
          <DialogDescription>
            Os funcionários selecionados terão o app bloqueado até enviarem a
            foto da folha de ponto da competência escolhida.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto flex-1 pr-1">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Competência (mês de referência)
            </label>
            <Input type="month" value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              data-testid="folha-competencia-input" />
          </div>

          <div className="relative">
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Adicionar funcionário individual (nome ou matrícula)
            </label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Digite para buscar…"
              data-testid="folha-search-input"
            />
            {(searchResults.length > 0 || searching) && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                {searching && (
                  <div className="px-3 py-2 text-xs text-slate-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />
                    Buscando…
                  </div>
                )}
                {searchResults.map((e) => (
                  <button key={e.id} type="button"
                    onClick={() => addIndividual(e)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 flex flex-col"
                    data-testid={`folha-search-result-${e.matricula}`}>
                    <span className="font-medium text-slate-800 truncate">{e.nome}</span>
                    <span className="text-[11px] text-slate-500 truncate">
                      {e.matricula}{e.posto_servico ? ` • ${e.posto_servico}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Por contrato
              </label>
              <select value={contrato} onChange={(e) => loadContrato(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="folha-contrato-select">
                <option value="">— escolha —</option>
                {contratos.map((c) => (
                  <option key={c.contract} value={c.contract}>
                    {c.contract} ({c.employees_count})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Por posto
              </label>
              <select value={posto} onChange={(e) => loadPosto(e.target.value)}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="folha-posto-select">
                <option value="">— escolha —</option>
                {postos.map((p) => (
                  <option key={p.posto || p} value={p.posto || p}>
                    {(p.posto || p)}{p.count ? ` (${p.count})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Ou planilha (XLSX)
              </label>
              <Button variant="outline" className="w-full h-9"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
                data-testid="folha-xlsx-btn">
                {importing
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <Upload className="w-4 h-4 mr-1.5" />}
                Importar XLSX
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden
                onChange={(e) => handleXlsx(e.target.files?.[0])} />
            </div>
          </div>

          {loadingEmps ? (
            <div className="py-6 text-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          ) : employees.length > 0 && (
            <>
              <div className="text-xs text-slate-600 font-medium">
                {selectedIds.length} de {employees.length} selecionado(s)
              </div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-[32vh] overflow-y-auto">
                {employees.map((e) => (
                  <label key={e.id}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={!!checked[e.id]}
                      onChange={() => setChecked((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                      className="rounded" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium text-slate-800">{e.nome}</span>
                      <span className="block text-[11px] text-slate-500 truncate">
                        {e.matricula}{e.posto_servico ? ` • ${e.posto_servico}` : ""}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleCreate}
            disabled={saving || selectedIds.length === 0}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="folha-create-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Convocar {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- envios / revisão -------------------- */
function SubmissionsDialog({ request, onClose }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoSub, setPhotoSub] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);

  const load = useCallback(async () => {
    if (!request) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/folha-ponto/requests/${request.id}/submissions`,
        { headers: authHeader(), timeout: 20000 }
      );
      setSubs(r.data.items || []);
    } catch {
      toast.error("Erro ao carregar envios");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { load(); }, [load]);

  const openPhoto = async (s) => {
    try {
      const r = await axios.get(
        `${API}/admin/folha-ponto/submissions/${s.id}/photo`,
        { headers: authHeader(), responseType: "blob", timeout: 60000 }
      );
      setPhotoUrl(URL.createObjectURL(r.data));
      setPhotoSub(s);
    } catch {
      toast.error("Erro ao abrir a foto");
    }
  };

  const review = async (s, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Motivo da rejeição (o funcionário verá):", "Foto ilegível — envie novamente") || "";
      if (reason === "") return;
    }
    setReviewingId(s.id);
    try {
      await axios.post(
        `${API}/admin/folha-ponto/submissions/${s.id}/review`,
        { action, reason },
        { headers: authHeader() }
      );
      toast.success(action === "approve" ? "Aprovada" : "Rejeitada — funcionário deverá reenviar");
      setPhotoUrl(null);
      setPhotoSub(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na revisão");
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <>
      <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Envios — {request ? compLabel(request.competencia) : ""}
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin inline" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {subs.map((s) => {
                const b = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
                return (
                  <div key={s.id}
                    className="flex items-center gap-2 px-3 py-2 text-sm"
                    data-testid={`folha-sub-${s.id}`}>
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium text-slate-800">{s.nome}</span>
                      <span className="block text-[11px] text-slate-500 truncate">
                        {s.matricula}{s.posto_servico ? ` • ${s.posto_servico}` : ""}
                        {s.submitted_at && ` • enviado ${new Date(s.submitted_at).toLocaleString("pt-BR")}`}
                        {s.status === "rejected" && s.reject_reason && ` • motivo: ${s.reject_reason}`}
                      </span>
                    </span>
                    <Badge className={`${b.cls} shrink-0`}>{b.label}</Badge>
                    {s.file_id && (
                      <Button variant="outline" size="sm" onClick={() => openPhoto(s)}
                        title="Ver foto" data-testid={`folha-photo-${s.id}`}>
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                    )}
                    {(s.status === "submitted" || s.status === "rejected") && s.file_id && (
                      <Button size="sm" onClick={() => review(s, "approve")}
                        disabled={reviewingId === s.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        title="Aprovar" data-testid={`folha-approve-${s.id}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    )}
                    {(s.status === "submitted" || s.status === "approved") && (
                      <Button variant="outline" size="sm" onClick={() => review(s, "reject")}
                        disabled={reviewingId === s.id}
                        className="text-red-600 hover:bg-red-50"
                        title="Rejeitar (pede reenvio)" data-testid={`folha-reject-${s.id}`}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {s.status === "pending" && (
                      <Clock className="w-4 h-4 text-slate-300 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoUrl} onOpenChange={(o) => { if (!o) { URL.revokeObjectURL(photoUrl); setPhotoUrl(null); setPhotoSub(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {photoSub?.nome} — {photoSub?.matricula}
            </DialogTitle>
          </DialogHeader>
          {photoUrl && (
            <img src={photoUrl} alt="Folha de ponto"
              className="w-full rounded-md border border-slate-200" />
          )}
          {photoSub && (photoSub.status === "submitted" || photoSub.status === "rejected") && (
            <DialogFooter>
              <Button variant="outline" onClick={() => review(photoSub, "reject")}
                className="text-red-600">
                <XCircle className="w-4 h-4 mr-1.5" /> Rejeitar
              </Button>
              <Button onClick={() => review(photoSub, "approve")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Aprovar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
